import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import EmptylMessageIcon from '@shared/assets/images/info_circle.svg';
import EmployeeModal from '@pages/admin/organization/components/EmployeeModal/EmployeeModal';
import { useUpdateCompanyMutation } from '@features/company/hooks';

import {
  useGetTeamInfoQuery,
  useUpdateTeamMutation,
  useAddTeamMutation,
} from '@features/team/hooks';
import RecipientModal from '@features/document-write/components/RecipientModal/RecipientModal';

import styles from '@pages/admin/organization/components/OrganizationDetailPanel/OrganizationDetailPanel.module.scss';
// 팀 옵션 상수화
const TEAM_ENABLED_OPTIONS = [
  { value: 0, label: '운영' },
  { value: 1, label: '중지' },
  { value: 2, label: '폐지' },
];
/**
 * OrganizationDetailPanel
 * - 회사 / 부서 상세 패널
 * - EmployeeModal 상태를 상위에서 공통 관리
 */
export default function OrganizationDetailPanel({
  selectedNode,
  onTeamCreateSuccess,
  onOpenAlert,
  onTeamDisabled,
  onDirtyChange,
  teamList,
  companyList,
}) {
  /** EmployeeModal 공통 상태 */
  const [employeeModal, setEmployeeModal] = useState({
    open: false,
    mode: null, // 'COMPANY_CEO' | 'TEAM_MANAGER' | 'TEAM_REF'
  });

  /** 회사 상세 폼 상태 (CompanyDetail에서 끌어올림) */
  const [companyForm, setCompanyForm] = useState(null);
  const [isCompanyDirty, setIsCompanyDirty] = useState(false);

  // team 상세 폼
  const [teamForm, setTeamForm] = useState(null);
  const [isTeamDirty, setIsTeamDirty] = useState(false);
  // 부서 생성용 관맂 상태
  const [createTeamManager, setCreateTeamManager] = useState(null);

  useEffect(() => {
    onDirtyChange?.(isCompanyDirty);
  }, [isCompanyDirty]);

  useEffect(() => {
    onDirtyChange?.(isTeamDirty);
  }, [isTeamDirty]);

  // 노드가 변경될 때 부서 생성용 관리자 상태 초기화
  useEffect(() => {
    if (selectedNode?.nodeType === 'TEAM_CREATE') {
      setCreateTeamManager(null);
      setTeamForm(null);
    }
  }, [selectedNode]);

  if (!selectedNode) {
    return (
      <div className={styles.emptyContainer}>
        <img src={EmptylMessageIcon} />
        <div className={styles.emptyContent}>
          조직도에서 회사나 부서를 선택하면 상세 정보를 확인할 수 있습니다.
        </div>
      </div>
    );
  }

  function openEmployeeModal(mode) {
    setEmployeeModal({ open: true, mode });
  }

  function closeEmployeeModal() {
    setEmployeeModal({ open: false, mode: null });
  }

  /**
   * EmployeeModal 선택 결과 공통 처리
   * @param {Array} selectedEmployees
   */
  function handleSelectEmployees(selectedEmployees) {
    if (!selectedEmployees || selectedEmployees.length === 0) return;

    const selected = selectedEmployees[0];

    if (employeeModal.mode === 'COMPANY_CEO') {
      setCompanyForm((prev) => ({
        ...prev,
        enumber: selected.enumber,
        ceoName: selected.name,
      }));
      setIsCompanyDirty(true);
    }

    if (employeeModal.mode === 'TEAM_MANAGER') {
      //  팀 수정화면이경우
      if (teamForm) {
        setTeamForm((prev) => ({
          ...prev,
          managerEnumber: selected.enumber,
          managerName: selected.name,
        }));
        setIsTeamDirty(true);
      }
      // 팀 추가 화면일 경우
      if (!teamForm) {
        setCreateTeamManager({
          enumber: selected.enumber,
          name: selected.name,
        });
      }
    }

    closeEmployeeModal();
  }

  return (
    <>
      {selectedNode.nodeType === 'COMPANY' && (
        <CompanyDetail
          company={selectedNode.raw}
          companyForm={companyForm}
          setCompanyForm={setCompanyForm}
          isDirty={isCompanyDirty}
          setIsDirty={setIsCompanyDirty}
          onOpenEmployeeModal={() => openEmployeeModal('COMPANY_CEO')}
          onOpenAlert={onOpenAlert}
          companyList={companyList}
        />
      )}

      {selectedNode.nodeType === 'TEAM' && (
        <TeamDetail
          team={selectedNode.raw}
          onOpenManagerModal={() => openEmployeeModal('TEAM_MANAGER')}
          teamForm={teamForm}
          setTeamForm={setTeamForm}
          isDirty={isTeamDirty}
          setIsDirty={setIsTeamDirty}
          onOpenAlert={onOpenAlert}
          onTeamDisabled={onTeamDisabled}
          teamList={teamList}
        />
      )}
      {selectedNode.nodeType === 'TEAM_CREATE' && (
        <TeamCreate
          selectedNode={selectedNode}
          onOpenManagerModal={() => openEmployeeModal('TEAM_MANAGER')}
          manager={createTeamManager}
          onSuccess={() => {
            onTeamCreateSuccess?.();
          }}
          onOpenAlert={onOpenAlert}
          teamList={teamList}
        />
      )}
      {/* {selectedNode.nodeType !== 'COMPANY' && selectedNode.nodeType !== 'TEAM' && (
        <div>알 수 없는 노드 타입입니다.</div>
      )} */}

      {/* 공통 EmployeeModal */}
      {employeeModal.open && (
        <EmployeeModal
          isOpen={employeeModal.open}
          onClose={closeEmployeeModal}
          onSelect={handleSelectEmployees}
          title={employeeModal.mode === 'COMPANY_CEO' ? '대표자 선택' : '관리자 선택'}
          selectedRecipients={
            employeeModal.mode === 'COMPANY_CEO' && companyForm?.enumber
              ? [
                  {
                    enumber: companyForm.enumber,
                    name: companyForm.ceoName,
                  },
                ]
              : employeeModal.mode === 'TEAM_MANAGER' && teamForm?.managerEnumber
                ? [
                    {
                      enumber: teamForm.managerEnumber,
                      name: teamForm.managerName,
                    },
                  ]
                : []
          }
        />
      )}
    </>
  );
}

/* ===========================
   CompanyDetail
=========================== */

function CompanyDetail({
  company,
  companyForm,
  setCompanyForm,
  isDirty,
  setIsDirty,
  onOpenEmployeeModal,
  onOpenAlert,
  companyList,
}) {
  const { mutate: updateCompany, isPending } = useUpdateCompanyMutation();

  useEffect(() => {
    if (!company) return;

    setCompanyForm({
      companyName: company.companyName ?? '',
      nick: company.nick ?? '',
      ceoName: company.ceoName ?? '',
      companyCode: company.companyCode,
      enumber: company.enumber,
    });

    setIsDirty(false);
  }, [company]);

  function handleSave() {
    // 회사명 중복 검사
    const nameDuplicate = companyList.some((c) => {
      const sameName = c.company?.trim() === companyForm.companyName.trim();
      const notMe = c.companyCode !== companyForm.companyCode;
      return sameName && notMe;
    });

    if (nameDuplicate) {
      onOpenAlert('이미 등록된 회사명입니다.');
      return;
    }

    // nick 중복 검사
    const nickDuplicate = companyList.some((c) => {
      const sameNick = c.nick?.trim() === companyForm.nick.trim();
      const notMe = c.companyCode !== companyForm.companyCode;
      return sameNick && notMe;
    });

    if (nickDuplicate) {
      onOpenAlert('이미 등록된 회사식별코드입니다.');
      return;
    }
    updateCompany(
      {
        companyCode: companyForm.companyCode,
        ceoEnumber: companyForm.enumber,
        companyName: companyForm.companyName,
        companyNick: companyForm.nick,
      },
      {
        onSuccess: (res) => {
          if (res.result_code !== 0) {
            //
            onOpenAlert(res.error_msg || '처리에 실패했습니다.');
            return;
          }

          setIsDirty(false);
          onOpenAlert('회사정보 수정 완료되었습니다.');
        },
      },
    );
  }

  if (!companyForm) return null;

  return (
    <div className={styles.contents}>
      <div className={styles.title}>회사 정보</div>

      <table className={styles.infoTable}>
        <tbody>
          <tr>
            <td className={styles.subTitle}>회사코드</td>
            <td>
              <input value={companyForm.companyCode} readOnly className={styles.input} />
            </td>
          </tr>

          <tr>
            <td className={styles.subTitle}>회사명</td>
            <td>
              <input
                value={companyForm.companyName}
                className={styles.input}
                onChange={(e) => {
                  setCompanyForm((prev) => ({
                    ...prev,
                    companyName: e.target.value,
                  }));
                  setIsDirty(true);
                }}
              />
            </td>
          </tr>

          <tr>
            <td className={styles.subTitle}>회사식별코드</td>
            <td>
              <input
                value={companyForm.nick}
                className={styles.input}
                onChange={(e) => {
                  setCompanyForm((prev) => ({
                    ...prev,
                    nick: e.target.value,
                  }));
                  setIsDirty(true);
                }}
              />
            </td>
          </tr>

          <tr>
            <td colSpan={2} className={styles.desc}>
              ※ 회사식별코드는 문서번호에 삽입되는 회사별 고유 이니셜입니다.
            </td>
          </tr>

          <tr>
            <td className={styles.subTitle}>대표자</td>
            <td>
              <div className={styles.inlineControl}>
                <button type="button" onClick={onOpenEmployeeModal}>
                  선택
                </button>
                <input value={companyForm.ceoName} readOnly />
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div className={styles.actionArea}>
        <button type="button" disabled={!isDirty || isPending} onClick={handleSave}>
          저장
        </button>
      </div>
    </div>
  );
}
// sort 유효성 검사
function validateSort(sort) {
  // null / undefined / 빈 문자열
  if (sort === null || sort === undefined || sort === '') {
    return '순서를 입력해주세요.';
  }

  const numberSort = Number(sort);

  // 숫자 아님
  if (Number.isNaN(numberSort)) {
    return '순서는 숫자만 입력할 수 있습니다.';
  }

  // 0 이하 방지
  if (numberSort <= 0) {
    return '순서는 1 이상의 숫자여야 합니다.';
  }

  return null; // 정상
}
/* ===========================
   TeamDetail
=========================== */

function TeamDetail({
  team,
  onOpenManagerModal,
  teamForm,
  setTeamForm,
  isDirty,
  setIsDirty,
  onOpenAlert,
  onTeamDisabled,
  teamList,
}) {
  const { mutate: updateTeam, isPending } = useUpdateTeamMutation();
  const teamCode = team?.teamCode;
  const [isRecipientModalOpen, setIsRecipientModalOpen] = useState(false);

  const { data: teamInfo, isLoading } = useGetTeamInfoQuery(teamCode, true);
  // 팀이 바뀔 때 이전 팀 상태 완전 초기화
  useEffect(() => {
    setTeamForm(null);
    setIsRecipientModalOpen(false);
  }, [teamCode]);

  /**
   *  팀 상세 초기화
   */
  useEffect(() => {
    if (!teamInfo) return;
    const normalizedRefusalList = (teamInfo.refusalReceiveList ?? []).map((emp) => ({
      ...emp,
      employeeTeamCode: emp.employeeTeamCode ?? emp.teamCode ?? emp.employeeTeam?.code ?? null,
    }));
    setTeamForm((prev) => {
      const refusalNames =
        Array.isArray(teamInfo.refusalReceiveList) && teamInfo.refusalReceiveList.length > 0
          ? teamInfo.refusalReceiveList.map((item) => item.name).join(', ')
          : '';

      return {
        teamCode: teamInfo.code ?? '',
        teamName: teamInfo.name ?? '',
        // 부모 팀 이름은 teamInfo에 없다면 team에서 유지하되, 나머지는 teamInfo 우선
        parentTeamName: teamInfo.parentTeamName || team?.parent?.team || '최상위 부서',

        managerEnumber: teamInfo.manager ?? 0,

        managerName: teamInfo.managerName ?? team?.name ?? '',

        refusalReceiveList: normalizedRefusalList ?? [],
        enabled: teamInfo.enabled ?? 0,
        sort: teamInfo.sort,
      };
    });

    setIsDirty(false);
  }, [teamInfo]);

  if (isLoading || !teamForm) return null;

  /**
   * 저장
   */
  function handleSave() {
    if (!teamForm) return;

    // 관리자 필수 체크
    // if (!teamForm.managerEnumber || teamForm.managerEnumber === 0) {
    //   onOpenAlert('관리자를 선택해주세요.');
    //   return;
    // }

    const isDuplicate = teamList.some((t) => {
      const sameParent = (t.departmentCode ?? null) === (teamInfo.department ?? null);

      const sameName = t.team?.trim() === teamForm.teamName.trim();

      const notMe = t.teamCode !== teamForm.teamCode;

      return sameParent && sameName && notMe;
    });

    if (isDuplicate) {
      onOpenAlert('같은 상위 조직 내에 동일한 부서명이 이미 존재합니다.');
      return;
    }
    // === sort 유효성 검사 추가 ===
    const sortError = validateSort(teamForm.sort);
    if (sortError) {
      onOpenAlert(sortError);
      return;
    }

    const senderList = teamForm.refusalReceiveList.map((emp) => {
      const employeeTeamCode = emp.employeeTeamCode ?? emp.teamCode;

      if (!employeeTeamCode) {
        throw new Error('employeeTeamCode 누락');
      }

      return {
        name: emp.name,
        code: emp.enumber,
        employeeTeamCode,
      };
    });
    console.log('senderList payload', senderList);
    updateTeam(
      {
        teamCode: teamForm.teamCode,
        company: team.companyCode,
        name: teamForm.teamName,
        manager: teamForm.managerEnumber || 0,
        department: teamInfo.department || 0,
        enabled: teamForm.enabled, // 운영상태 추가
        sort: teamForm.sort, // 순서 추가
        senderList,
      },
      {
        onSuccess: (res) => {
          if (res.response.data.result_code !== 0) {
            //
            onOpenAlert(res.response.data.error_msg || '처리에 실패했습니다.');
            return;
          }
          onOpenAlert('부서정보가 수정되었습니다.', () => {
            if (teamForm.enabled !== 0) {
              onTeamDisabled?.();
            }
          });
          setIsDirty(false);
        },
        onError: (res) => {
          console.log(res);
          onOpenAlert(res.response.data.error_msg || '처리에 실패했습니다.');
          return;
        },
      },
    );
  }

  return (
    <>
      <div className={styles.contents}>
        <div className={styles.title}>부서 정보</div>

        <table className={styles.infoTable}>
          <tbody>
            <tr>
              <td className={styles.subTitle}>상위부서</td>
              <td>
                <input className={styles.input} value={teamForm.parentTeamName} readOnly />
              </td>
            </tr>

            <tr>
              <td className={styles.subTitle}>부서코드</td>
              <td>
                <input className={styles.input} value={teamForm.teamCode} readOnly />
              </td>
            </tr>

            <tr>
              <td className={styles.subTitle}>부서명</td>
              <td>
                <input
                  className={styles.input}
                  value={teamForm.teamName}
                  onChange={(e) => {
                    setTeamForm((prev) => ({
                      ...prev,
                      teamName: e.target.value,
                    }));
                    setIsDirty(true);
                  }}
                />
              </td>
            </tr>

            <tr>
              <td className={styles.subTitle}>관리자</td>
              <td>
                <div className={styles.inlineControl}>
                  <button type="button" onClick={onOpenManagerModal}>
                    선택
                  </button>
                  <input value={teamForm.managerName} readOnly />
                </div>
              </td>
            </tr>
            <tr>
              <td className={styles.subTitle}>참조 및 시행 제외 대상</td>
              <td className={styles.topAlign}>
                <div className={styles.refusalField}>
                  <div className={styles.refusalBox}>
                    {teamForm.refusalReceiveList.length === 0 && (
                      <div className={styles.placeholder}>선택된 대상이 없습니다.</div>
                    )}

                    {teamForm.refusalReceiveList.map((e) => (
                      <div key={e.enumber} className={styles.refusalRow}>
                        <span className={styles.refusalText}>
                          [{e.team}] {e.name}
                        </span>

                        <button
                          type="button"
                          className={styles.removeButton}
                          onClick={() => {
                            setTeamForm((prev) => {
                              const next = prev.refusalReceiveList.filter(
                                (item) => item.enumber !== e.enumber,
                              );

                              return {
                                ...prev,
                                refusalReceiveList: next,
                              };
                            });

                            setIsDirty(true);
                          }}
                        >
                          삭제
                        </button>
                      </div>
                    ))}
                  </div>

                  <button
                    type="button"
                    className={styles.selectButton}
                    onClick={() => setIsRecipientModalOpen(true)}
                  >
                    선택
                  </button>
                </div>
              </td>
            </tr>
            <tr>
              <td className={styles.subTitle}>운영상태</td>
              <td>
                <select
                  className={styles.select}
                  value={teamForm.enabled}
                  onChange={(e) => {
                    setTeamForm((prev) => ({
                      ...prev,
                      enabled: Number(e.target.value),
                    }));
                    setIsDirty(true);
                  }}
                >
                  {TEAM_ENABLED_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </td>
            </tr>
            <tr>
              <td className={styles.subTitle}>순서</td>
              <td>
                <input
                  className={styles.input}
                  value={teamForm.sort}
                  onChange={(e) => {
                    const value = e.target.value;

                    // 숫자만 허용 (빈 문자열은 허용)
                    if (value !== '' && !/^\d+$/.test(value)) return;

                    setTeamForm((prev) => ({
                      ...prev,
                      sort: value,
                    }));
                    setIsDirty(true);
                  }}
                />
              </td>
            </tr>
          </tbody>
        </table>

        <div className={styles.actionArea}>
          <button type="button" disabled={!isDirty} onClick={handleSave}>
            저장
          </button>
        </div>
      </div>
      {isRecipientModalOpen && (
        <RecipientModal
          key={teamCode}
          isOpen={isRecipientModalOpen}
          title="참조 및 시행 제외 대상 선택"
          selectedRecipients={teamForm.refusalReceiveList}
          onClose={() => setIsRecipientModalOpen(false)}
          applyRejectFilter={false}
          onSelect={(selectedEmployees) => {
            const normalized = selectedEmployees.map((emp) => ({
              ...emp,
              employeeTeamCode: emp.employeeTeamCode ?? emp.teamCode ?? emp.employeeTeam?.code,
            }));

            setTeamForm((prev) => ({
              ...prev,
              refusalReceiveList: normalized,
            }));

            setIsDirty(true);
            setIsRecipientModalOpen(false);
          }}
        />
      )}
    </>
  );
}
//  팀 추가
function TeamCreate({
  selectedNode,
  onOpenAlert,
  onOpenManagerModal,
  manager,
  onSuccess,
  teamList,
}) {
  const [refusalReceiveList, setRefusalReceiveList] = useState([]);
  const [isRecipientModalOpen, setIsRecipientModalOpen] = useState(false);
  const [enabled, setEnabled] = useState(0);
  const [sort, setSort] = useState(1);
  const parent = selectedNode.parent;

  const isCompanyParent = parent.nodeType === 'COMPANY';

  // 회사코드
  const companyCode = parent.raw.companyCode;

  // 새부서의 상위 코드
  const parentTeamCode = isCompanyParent
    ? null // 회사 바로 아래면 department = null
    : parent.raw.teamCode;

  const parentLabel = parent.label;

  const { mutate: addTeam, isPending } = useAddTeamMutation();

  const [teamName, setTeamName] = useState('');
  const [managerEnumber, setManagerEnumber] = useState(0);
  const [managerName, setManagerName] = useState('');

  // 관리자 선택 반영
  useEffect(() => {
    if (manager) {
      setManagerEnumber(manager.enumber);
      setManagerName(manager.name);
    } else {
      setManagerEnumber(0);
      setManagerName('');
    }
  }, [manager]);

  // 저장 핸들러
  function handleSave() {
    if (!teamName.trim()) {
      onOpenAlert('부서명을 입력해주세요.');
      return;
    }

    // 관리자 필수 체크 추가
    // if (!managerEnumber || managerEnumber === 0) {
    //   onOpenAlert('관리자를 선택해주세요.');
    //   return;
    // }

    //  형제 부서 중복 검사
    const isDuplicate = teamList.some((team) => {
      const sameParent = (team.departmentCode ?? null) === (parentTeamCode ?? null);

      const sameName = team.team?.trim() === teamName.trim();

      return sameParent && sameName;
    });

    if (isDuplicate) {
      onOpenAlert('같은 상위 조직 내에 동일한 부서명이 이미 존재합니다.');
      return;
    }
    // === sort 유효성 검사 추가 ===
    const sortError = validateSort(sort);
    if (sortError) {
      onOpenAlert(sortError);
      return;
    }
    const senderList =
      refusalReceiveList.length > 0
        ? refusalReceiveList.map((emp) => ({
            name: emp.name,
            code: emp.enumber,
            employeeTeamCode: emp.teamCode,
          }))
        : null;

    console.log('senderList payload', senderList);

    addTeam(
      {
        company: companyCode,
        name: teamName,
        department: parentTeamCode,
        manager: managerEnumber || 0,
        enabled,
        sort,
        senderList,
      },
      {
        onSuccess() {
          setTeamName('');
          setManagerEnumber(0);
          setManagerName('');
          onSuccess?.();
        },
      },
    );
  }
  return (
    <>
      <div className={styles.contents}>
        <div className={styles.title}>새 부서 생성</div>

        <table className={styles.infoTable}>
          <tbody>
            <tr>
              <td className={styles.subTitle}>상위 조직</td>
              <td>
                <input className={styles.input} value={parentLabel} readOnly />
              </td>
            </tr>

            <tr>
              <td className={styles.subTitle}>부서명</td>
              <td>
                <input
                  className={styles.input}
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                />
              </td>
            </tr>
            <tr>
              <td className={styles.subTitle}>관리자</td>
              <td>
                <div className={styles.inlineControl}>
                  <button type="button" onClick={onOpenManagerModal}>
                    선택
                  </button>
                  <input value={managerName} readOnly />
                </div>
              </td>
            </tr>
            <tr>
              <td className={styles.subTitle}>참조 및 시행 제외 대상</td>
              <td>
                <div className={styles.refusalField}>
                  <div className={styles.refusalBox}>
                    {refusalReceiveList.length === 0 && (
                      <div className={styles.placeholder}>선택된 대상이 없습니다.</div>
                    )}

                    {refusalReceiveList.map((e) => (
                      <div key={e.enumber} className={styles.refusalRow}>
                        <span className={styles.refusalText}>
                          [{e.team}] {e.name}
                        </span>
                        <button
                          type="button"
                          className={styles.removeButton}
                          onClick={() =>
                            setRefusalReceiveList((prev) =>
                              prev.filter((item) => item.enumber !== e.enumber),
                            )
                          }
                        >
                          삭제
                        </button>
                      </div>
                    ))}
                  </div>

                  <button
                    type="button"
                    className={styles.selectButton}
                    onClick={() => setIsRecipientModalOpen(true)}
                  >
                    선택
                  </button>
                </div>
              </td>
            </tr>
            <tr>
              <td className={styles.subTitle}>운영상태</td>
              <td>
                <select
                  className={styles.select}
                  value={enabled}
                  onChange={(e) => setEnabled(Number(e.target.value))}
                >
                  {TEAM_ENABLED_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </td>
            </tr>
            <tr>
              <td className={styles.subTitle}>순서</td>
              <td>
                <input
                  className={styles.input}
                  value={sort}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value !== '' && !/^\d+$/.test(value)) return;
                    setSort(value);
                  }}
                />
              </td>
            </tr>
          </tbody>
        </table>

        <div className={styles.actionArea}>
          <button type="button" disabled={isPending} onClick={handleSave}>
            저장
          </button>
        </div>
      </div>
      {isRecipientModalOpen && (
        <RecipientModal
          isOpen
          title="참조 및 시행 제외 대상 선택"
          selectedRecipients={refusalReceiveList}
          applyRejectFilter={false}
          onClose={() => setIsRecipientModalOpen(false)}
          onSelect={(selectedEmployees) => {
            setRefusalReceiveList(selectedEmployees);
            setIsRecipientModalOpen(false);
          }}
        />
      )}
    </>
  );
}
OrganizationDetailPanel.propTypes = {
  selectedNode: PropTypes.object,
};
